/** @jsxImportSource @emotion/react */
import { css } from "@emotion/react";
import styled from "@emotion/styled";
import { TDevice, TDeviceConfig, getImportConfigDevices, setImportConfigDevices } from "apis/visitors/device";
import { useToast } from "hook/useToast";
import { useEffect, useState } from "react";
import Modal from "react-modal";
import { theme } from "styles/theme";
import Button from "uis/Button";

const modalStyle = {
  content: {
    top: "50%",
    left: "50%",
    right: "auto",
    bottom: "auto",
    marginRight: "-50%",
    width: "562px",
    borderRadius: "20px",
    transform: "translate(-50%, -50%)",
  },
  overlay: {
    backgroundColor: "var(--component-color-material-dimmer)",
    zIndex: 1000,
  },
};
Modal.setAppElement("#root");

type Props = {
  modalIsOpen: boolean;
  closeModal: () => void;
  onChange: () => void;
  device: TDevice;
};

const ModalContainer = styled.div`
  padding: 40px 30px;
  display: flex;
  flex-direction: column;
`;

const Title = styled.p`
  font-size: 20px;
  font-weight: 500;
  line-height: 150%;
  text-align: center;
  margin-bottom: 4px;
`;

const SubTitle = styled.p`
  color: ${theme.neutral600};
  font-size: 16px;
  font-weight: 400px;
  line-height: 150%;
  text-align: center;
`;

const DeviceRow = styled.div<{ selected?: boolean }>`
  color: ${theme.neutral800};
  font-size: 16px;
  font-weight: ${(props) => (props.selected ? 600 : 400)};
  background-color: ${(props) => (props.selected ? theme.neutral50 : "white")};
  border-radius: 10px;
  padding: 22px 18px;
  &:hover {
    background-color: ${theme.neutral50};
  }
`;

export default function DeviceConfigModal(props: Props) {
  const { modalIsOpen, closeModal, onChange, device } = props;
  const [deviceConfigs, setDeviceConfigs] = useState<TDeviceConfig[]>([]);
  const [selected, setSelected] = useState<TDeviceConfig>();
  const { showToast } = useToast();

  useEffect(() => {
    if (modalIsOpen) {
      getImportConfigDevices(device.id).then((res) => {
        setDeviceConfigs(res);
      });
    }
  }, [modalIsOpen]);

  const onSaveSetting = async () => {
    if (selected) {
      const res = await setImportConfigDevices(selected?.id, device.id);
      if (res) {
        onChange();
        closeModal();
      } else {
        showToast("설정 정보를 가져오는데 실패했습니다.", "error");
      }
    } else {
      showToast("설정 정보를 가져올 기기를 선택해주세요.", "warning");
    }
  };

  return (
    <Modal isOpen={modalIsOpen} onRequestClose={closeModal} style={modalStyle}>
      <ModalContainer>
        <Title>설정 정보를 가져올 기기를 선택해주세요.</Title>
        <SubTitle>
          기기 설정 정보를 불러오면 기존 설정 값이 초기화되고,
          <br />
          선택한 기기의 설정값으로 덮어씁니다.
          <span
            css={css`
              color: #ff3e3e;
            `}
          >
            (활성화 여부, 담당자 설정 모두)
          </span>
        </SubTitle>

        <div
          css={css`
            margin: 24px 0;
            border-radius: 20px;
            border: 1px solid ${theme.neutral50};
          `}
        >
          {deviceConfigs.map((item, index) => {
            return (
              <DeviceRow
                key={index}
                onClick={() => setSelected(item)}
                selected={item.deviceName === selected?.deviceName}
              >
                {item.deviceName}
              </DeviceRow>
            );
          })}
        </div>
        <div
          css={css`
            display: flex;
            gap: 24px;
          `}
        >
          <Button
            onClick={closeModal}
            title="취소"
            type="secondary"
            styles={{
              container: css`
                flex: 1;
                height: 64px;
              `,
              text: css`
                font-size: 20px;
                font-weight: 700;
              `,
            }}
          />
          <Button
            onClick={onSaveSetting}
            title="설정 덮어쓰기"
            styles={{
              container: css`
                flex: 1;
                height: 64px;
              `,
              text: css`
                font-size: 20px;
                font-weight: 700;
              `,
            }}
          />
        </div>
      </ModalContainer>
    </Modal>
  );
}
